// src/App.js
import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './hooks/useToast';
import ProtectedRoute from './components/auth/ProtectedRoute';
import OfferPopup from './components/public/common/OfferPopup';
import publicData from './data/publicData.json';

// Public Pages (not lazy loaded for better initial load performance)
import HomePage from './pages/public/home/HomePage';
import AboutPage from './pages/public/about/AboutPage';
import BlogListPage from './pages/public/blog/BlogListPage';
import BlogDetailPage from './pages/public/blog/BlogDetailPage';
import ContactPage from './pages/public/contact/ContactPage';
import ServicePage from './pages/public/services/ServicePage';
import ServiceDetailPage from './pages/public/services/ServiceDetailPage';
import CaseStudyListPage from './pages/public/case-studies/CaseStudyListPage';
import CaseStudyDetailPage from './pages/public/case-studies/CaseStudyDetailPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import NotFoundPage from './pages/public/error/NotFoundPage';
import ContentPage from './pages/public/content/ContentPage';
import ContentListPagePublic from './pages/public/content/ContentListPage';
import SitemapPage from './pages/public/sitemap/SitemapPage';

// Lazy load admin components
const AdminDashboard = lazy(() => import('./pages/admin/dashboard/DashboardPage'));
const AdminBlogs = lazy(() => import('./pages/admin/blogs/BlogListPage'));
const AdminBlogForm = lazy(() => import('./pages/admin/blogs/BlogFormPage'));
const AdminUsers = lazy(() => import('./pages/admin/users/UserListPage'));
const AdminUserForm = lazy(() => import('./pages/admin/users/UserFormPage'));
const AdminProfile = lazy(() => import('./pages/admin/profile/ProfilePage'));
const AdminMedia = lazy(() => import('./pages/admin/media/MediaPage'));
const AdminCaseStudies = lazy(() => import('./pages/admin/case-studies/CaseStudyListPage'));
const AdminCaseStudyForm = lazy(() => import('./pages/admin/case-studies/CaseStudyFormPage'));
const AdminServices = lazy(() => import('./pages/admin/services/ServiceListPage'));
const AdminServiceForm = lazy(() => import('./pages/admin/services/ServiceFormPage'));
const AdminForums = lazy(() => import('./pages/admin/forums/ForumListPage'));
const AdminComments = lazy(() => import('./pages/admin/comments/CommentListPage'));
const AdminCategories = lazy(() => import('./pages/admin/categories/CategoryListPage'));
const AdminTags = lazy(() => import('./pages/admin/tags/TagListPage'));
const AdminCompany = lazy(() => import('./pages/admin/company/CompanyPage'));
const AdminContent = lazy(() => import('./pages/admin/content/ContentPage'));
const AdminProjects = lazy(() => import('./pages/admin/projects/ProjectListPage'));
const AdminCalendar = lazy(() => import('./pages/admin/calendar/CalendarPage'));
const AdminReports = lazy(() => import('./pages/admin/reports/ReportsPage'));
const AdminNewsletter = lazy(() => import('./pages/admin/newsletter/NewsletterPage'));
const AdminContact = lazy(() => import('./pages/admin/contact/ContactPage'));
const AdminLLMs = lazy(() => import('./pages/admin/llms/LLMListPage'));
const AdminLLMForm = lazy(() => import('./pages/admin/llms/LLMFormPage'));
const AdminSystemLogs = lazy(() => import('./pages/admin/system-logs/SystemLogsPage'));
const AdminContentGenerator = lazy(() => import('./pages/admin/content-generator/ContentGeneratorPage'));
const AdminContentGeneratorWizard = lazy(() => import('./pages/admin/content-generator/ContentGeneratorWizard'));
const AdminContentGeneratorFormPage = lazy(() => import('./pages/admin/content-generator/ContentGeneratorFormPage'));
const AdminContentGeneratorViewPage = lazy(() => import('./pages/admin/content-generator/ContentGeneratorViewPage'));
const AdminPrompts = lazy(() => import('./pages/admin/prompts/PromptListPage'));
const AdminPromptForm = lazy(() => import('./pages/admin/prompts/PromptFormPage'));
const AdminN8n = lazy(() => import('./pages/admin/n8n/N8nPage'));

// AI Orchestration Pages
const AIOrchestratorDashboard = lazy(() => import('./pages/admin/ai-orchestrator/AIOrchestratorDashboard'));
const AIOrchestratorForm = lazy(() => import('./pages/admin/ai-orchestrator/AIOrchestratorForm'));
const AIOrchestratorView = lazy(() => import('./pages/admin/ai-orchestrator/AIOrchestratorView'));
const AITaskQueue = lazy(() => import('./pages/admin/ai-orchestrator/AITaskQueue'));

// Import AI Orchestrator pages
import AIOrchestratorAgents from './pages/admin/ai-orchestrator/AIOrchestratorAgents';
import AIOrchestratorTasks from './pages/admin/ai-orchestrator/AIOrchestratorTasks';
import AIOrchestratorMemory from './pages/admin/ai-orchestrator/AIOrchestratorMemory';
import AIOrchestratorSettings from './pages/admin/ai-orchestrator/AIOrchestratorSettings';
import AIOrchestratorStatsPage from './pages/admin/ai-orchestrator/AIOrchestratorStatsPage';

const App = () => {
  useEffect(() => {
    // Get GTM ID from environment variable or fallback to publicData
    const gtmId = process.env.REACT_APP_GTM_ID || publicData.company.analytics.gtm.id;

    // Initialize Google Tag Manager
    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.appendChild(script);

    // Add noscript iframe for GTM
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    return () => {
      // Cleanup
      document.head.removeChild(script);
      if (document.body.contains(noscript)) {
        document.body.removeChild(noscript);
      }
    };
  }, []);

  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <ErrorBoundary>
            <Router>
              <Suspense fallback={<LoadingSpinner />}>
                <div className="relative min-h-screen">
                  <OfferPopup />
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<HomePage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/blog" element={<BlogListPage />} />
                    <Route path="/blog/:urlSlug" element={<BlogDetailPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/services" element={<ServicePage />} />
                    <Route path="/services/:slug" element={<ServiceDetailPage />} />
                    <Route path="/case-studies" element={<CaseStudyListPage />} />
                    <Route path="/case-studies/:urlSlug" element={<CaseStudyDetailPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/legal" element={<ContentListPagePublic category="legal" />} />
                    <Route path="/support" element={<ContentListPagePublic category="support" />} />
                    <Route path="/legal/:slug" element={<ContentPage category="legal" />} />
                    <Route path="/support/:slug" element={<ContentPage category="support" />} />
                    <Route path="/sitemap" element={<SitemapPage />} />

                    {/* Protected Admin Routes */}
                    <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                    <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                    <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
                    <Route path="/admin/users/new" element={<ProtectedRoute><AdminUserForm /></ProtectedRoute>} />
                    <Route path="/admin/users/edit/:userId" element={<ProtectedRoute><AdminUserForm /></ProtectedRoute>} />
                    <Route path="/admin/profile" element={<ProtectedRoute><AdminProfile /></ProtectedRoute>} />
                    <Route path="/admin/blogs" element={<ProtectedRoute><AdminBlogs /></ProtectedRoute>} />
                    <Route path="/admin/blogs/new" element={<ProtectedRoute><AdminBlogForm /></ProtectedRoute>} />
                    <Route path="/admin/blogs/:mode/:id" element={<ProtectedRoute><AdminBlogForm /></ProtectedRoute>} />
                    <Route path="/admin/media" element={<ProtectedRoute><AdminMedia /></ProtectedRoute>} />
                    <Route path="/admin/case-studies" element={<ProtectedRoute><AdminCaseStudies /></ProtectedRoute>} />
                    <Route path="/admin/case-studies/new" element={<ProtectedRoute><AdminCaseStudyForm /></ProtectedRoute>} />
                    <Route path="/admin/case-studies/:id" element={<ProtectedRoute><AdminCaseStudyForm /></ProtectedRoute>} />
                    <Route path="/admin/services" element={<ProtectedRoute><AdminServices /></ProtectedRoute>} />
                    <Route path="/admin/services/new" element={<ProtectedRoute><AdminServiceForm /></ProtectedRoute>} />
                    <Route path="/admin/services/:slug/edit" element={<ProtectedRoute><AdminServiceForm /></ProtectedRoute>} />
                    <Route path="/admin/forums" element={<ProtectedRoute><AdminForums /></ProtectedRoute>} />
                    <Route path="/admin/comments" element={<ProtectedRoute><AdminComments /></ProtectedRoute>} />
                    <Route path="/admin/categories" element={<ProtectedRoute><AdminCategories /></ProtectedRoute>} />
                    <Route path="/admin/tags" element={<ProtectedRoute><AdminTags /></ProtectedRoute>} />
                    <Route path="/admin/company/*" element={<ProtectedRoute><AdminCompany /></ProtectedRoute>} />
                    <Route path="/admin/content/*" element={<ProtectedRoute><AdminContent /></ProtectedRoute>} />
                    <Route path="/admin/projects" element={<ProtectedRoute><AdminProjects /></ProtectedRoute>} />
                    <Route path="/admin/calendar" element={<ProtectedRoute><AdminCalendar /></ProtectedRoute>} />
                    <Route path="/admin/reports" element={<ProtectedRoute><AdminReports /></ProtectedRoute>} />
                    <Route path="/admin/newsletter" element={<ProtectedRoute><AdminNewsletter /></ProtectedRoute>} />
                    <Route path="/admin/contact" element={<ProtectedRoute><AdminContact /></ProtectedRoute>} />
                    <Route path="/admin/llms" element={<ProtectedRoute><AdminLLMs /></ProtectedRoute>} />
                    <Route path="/admin/llms/create" element={<ProtectedRoute><AdminLLMForm /></ProtectedRoute>} />
                    <Route path="/admin/llms/edit/:id" element={<ProtectedRoute><AdminLLMForm /></ProtectedRoute>} />
                    <Route path="/admin/system-logs" element={<ProtectedRoute><AdminSystemLogs /></ProtectedRoute>} />
                    <Route path="/admin/content-generator" element={<ProtectedRoute><AdminContentGenerator /></ProtectedRoute>} />
                    <Route path="/admin/content-generator/new" element={<ProtectedRoute><AdminContentGeneratorWizard /></ProtectedRoute>} />
                    <Route path="/admin/content-generator/:id" element={<ProtectedRoute><AdminContentGeneratorWizard /></ProtectedRoute>} />
                    <Route path="/admin/content-generator/view/:id" element={<ProtectedRoute><AdminContentGeneratorViewPage /></ProtectedRoute>} />
                    <Route path="/admin/content-generator/edit/:id" element={<ProtectedRoute><AdminContentGeneratorWizard /></ProtectedRoute>} />
                    <Route path="/admin/prompts" element={<ProtectedRoute><AdminPrompts /></ProtectedRoute>} />
                    <Route path="/admin/prompts/create" element={<ProtectedRoute><AdminPromptForm /></ProtectedRoute>} />
                    <Route path="/admin/prompts/edit/:id" element={<ProtectedRoute><AdminPromptForm /></ProtectedRoute>} />
                    <Route path="/admin/n8n" element={<ProtectedRoute><AdminN8n /></ProtectedRoute>} />

                    {/* AI Orchestration Routes */}
                    <Route path="/admin/ai-orchestrator" element={<ProtectedRoute><AIOrchestratorDashboard /></ProtectedRoute>} />
                    <Route path="/admin/ai-orchestrator/dashboard" element={<ProtectedRoute><AIOrchestratorDashboard /></ProtectedRoute>} />
                    <Route path="/admin/ai-orchestrator/new" element={<ProtectedRoute><AIOrchestratorForm /></ProtectedRoute>} />
                    <Route path="/admin/ai-orchestrator/:id" element={<ProtectedRoute><AIOrchestratorView /></ProtectedRoute>} />
                    <Route path="/admin/ai-orchestrator/edit/:id" element={<ProtectedRoute><AIOrchestratorForm /></ProtectedRoute>} />
                    <Route path="/admin/ai-orchestrator/:id/agents" element={<ProtectedRoute><AIOrchestratorAgents /></ProtectedRoute>} />
                    <Route path="/admin/ai-orchestrator/:id/tasks" element={<ProtectedRoute><AIOrchestratorTasks /></ProtectedRoute>} />
                    <Route path="/admin/ai-orchestrator/:id/memory" element={<ProtectedRoute><AIOrchestratorMemory /></ProtectedRoute>} />
                    <Route path="/admin/ai-orchestrator/:id/settings" element={<ProtectedRoute><AIOrchestratorSettings /></ProtectedRoute>} />
                    <Route path="/admin/ai-orchestrator/:id/stats" element={<ProtectedRoute><AIOrchestratorStatsPage /></ProtectedRoute>} />

                    {/* 404 Route */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                  <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="colored"
                  />
                </div>
              </Suspense>
            </Router>
          </ErrorBoundary>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
};

export default App;
